const pluralize = (count, one, many) => (count === 1 ? one : many);

/**
 * truncate - Truncates a string to less-than the given `maxLen`. It will
 * attempt to truncate at a word boundary. If a word boundary isn't available,
 * it will trucate to maxLen. Regardless, `...` is appended to any truncated
 * string. If the original string is less than the given `maxLen`, the string
 * is returned as-is.
 *
 * @param {string} str    A string to truncate.
 * @param {number} maxLen Maximum allowed length for the string to end up.
 *
 * @returns {string} The truncated string. (or the original string if it was
 * less than the maxLen.)
 */
const truncate = (str, maxLen) => {
  if (str.length <= maxLen) {
    return str;
  }

  const shortStr = str.substr(0, maxLen);

  // Matches the last one-or-more whitespace in a string, including any
  // characters that follow the whitespace.
  const match = /\s+\S*$/g.exec(shortStr);
  return match ? `${shortStr.substring(0, match.index)}...` : `${shortStr}...`;
};

const makeLinkValid = string =>
  string.startsWith('http://') || string.startsWith('https://')
    ? string
    : `http://${string}`;

const displayStringElse = (str, elsedDisplayText) => (str?.length ? str : elsedDisplayText);

const capitalizeFirstLetter = string => string[0].toUpperCase() + string.slice(1);

/**
 * showComma - Returns a comma or an empty string depending on element position in array
 *
 * @param {array}  arr Array to test
 * @param {number} idx Current position in array
 *
 * @return {string} Comma or empty string
 */
const showComma = (arr, idx) => (arr.length === 1 || arr.length - 1 === idx ? '' : ', ');

/**
 * removeAllSpaces - Removes all spaces from a string
 *
 * @param {string} str String with spaces
 *
 * @return {string} String without spaces
 */
const removeAllSpaces = str => str.replace(/\s/g, '');

/**
 * filterStudentInput - Filters out unsupported characters out of a string.
 * https://github.com/eduphoria/SchoolObjects.NET/issues/17777
 * https://github.com/eduphoria/SchoolObjects.NET/issues/17998
 *
 * @param {string} text String from which to remove these characters.
 *
 * @returns {string} String without these characters.
 */
export const filterStudentInput = text =>
  (text || '').replace(
    // Filter out all non-printable ASCII control characters except for tab, line feed, carriage return
    // eslint-disable-next-line
    /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g,
    ''
  );

/**
 * Adapted from common/ckeditor-plugins/wordcount.js so that counts match between
 * CKEditor and Enter Answers
 *
 * Creates a DOM element then leverages browser API to strip HTML
 */
const stripHTML = html => {
  const tempDiv = document.createElement('div');

  tempDiv.innerHTML = html;

  if (tempDiv.textContent === '' && typeof tempDiv.innerText === 'undefined') {
    return '';
  }

  return tempDiv.textContent || tempDiv.innerText;
};

/**
 * Adapted from common/ckeditor-plugins/wordcount.js so that counts match between
 * CKEditor and Enter Answers
 */
const getCharacterCount = text => {
  /**
   * Remove any line break and spaces and replace &nbsp; with ' '
   *
   * \r - Carrriage Return
   * \n - Line Feed
   * \s - Spaces
   * gm - global and multiline
   * gi - global and case insensitive
   */
  const normalizedText = text.replace(/(\r\n|\n|\r|\s)/gm, '').replace(/&nbsp;/gi, '');

  /**
   * Strip, Remove any remaining tab or line breaks, or surrounding whitespace
   * then return length
   *
   * ^  - Beginning of string
   * \t - Tab
   * \r - Carriage Return
   * \n - Line Feed
   * $ - End of string
   */
  return stripHTML(normalizedText).replace(/^([\t\r\n]*)$/, '').length;
};

/**
 * Adapted from common/ckeditor-plugins/wordcount.js so that counts match between
 * CKEditor and Enter Answers
 */
const getWordCount = text => {
  /**
   * Remove linebreaks, surrounding whitespace, and replace &nbsp; with ' '
   * \r - Carrriage Return
   * \n - Line Feed
   * gm - global and multiline
   * g  - global
   * \s - any whitepsace character
   */
  const normalizedText = text
    .replace(/(\r\n|\n|\r)/gm, ' ')
    .replace(/^\s+|\s+$/g, '')
    .replace('&nbsp;', ' ');

  const strippedText = stripHTML(normalizedText);
  const WORD_DELIMITER = /\s+/;

  // Split stripped text into words
  const words = strippedText.split(WORD_DELIMITER);

  // Filter anything that's falsy or that doesn't match word delimiter and return length
  return words.filter((word, index) => words[index] || !words[index].match(WORD_DELIMITER))
    .length;
};

export {
  capitalizeFirstLetter,
  displayStringElse,
  getCharacterCount,
  getWordCount,
  makeLinkValid,
  pluralize,
  removeAllSpaces,
  showComma,
  truncate
};
